import dayjs from 'dayjs';
import { find, isEqual, isEmpty, isObject, isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useInputSelect = ({ defaultValue = undefined, field, setInputs, config }) => {
  const [text, setText] = useState(null);
  const isTouched = useRef(null);

  const { selectOptions, selectGroupBy, readonly, is_required, forceUpdate } = config;

  const options = useMemo(() => {
    return selectOptions || [];
  }, [selectOptions]);

  const groupBy = useMemo(() => {
    return selectGroupBy || undefined;
  }, [selectGroupBy]);

  const errorMessage = useMemo(() => {
    const selected = find(options, { _id: text?._id || text }) || text;
    if (is_required && isEmpty(selected)) {
      return '必填';
    }
    return false;
  }, [is_required, options, text]);

  const updateInputs = useCallback(
    (text) => {
      if (readonly) return;
      const selected = find(options, { _id: text?._id || text }) || text;
      setInputs((v) => {
        return {
          ...v,
          [field]: {
            value: selected?._id || text?._id || text,
            touched: forceUpdate ? true : !!isTouched.current,
            error: is_required ? isEmpty(selected) : false
          }
        };
      });
    },
    [field, readonly, setInputs, errorMessage, is_required, forceUpdate]
  );

  const customSetText = (value) => {
    const option = find(options, { label: value?.label || value });
    setText(value);
    updateInputs(option || value);
  };

  useEffect(() => {
    if (!isUndefined(defaultValue)) {
      const value = isObject(defaultValue) ? defaultValue?._id : defaultValue;
      if (isEqual(value, text?._id)) return;
      if (defaultValue) {
        if (isObject(defaultValue) && defaultValue._id && defaultValue.label) {
          customSetText(defaultValue);
        } else {
          const option = find(options, { _id: defaultValue });
          if (option) {
            customSetText(option);
          } else {
            customSetText({ _id: defaultValue, label: defaultValue });
          }
        }
      } else {
        customSetText(defaultValue);
      }
    } else if (is_required) {
      customSetText(null);
    }
    return () => {
      setInputs((v) => {
        return {
          ...v,
          [field]: undefined
        };
      });
    };
  }, [defaultValue, is_required]);

  return {
    errorMessage,
    options,
    groupBy,
    isTouched,
    text,
    setText: (v) => {
      if (!isTouched.current) {
        isTouched.current = true;
      }
      customSetText(v);
    }
  };
};
